import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowLeft, LayoutGrid, Pencil, Plus, Trash2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import { Label } from "@/shared/components/ui/label";
import { Separator } from "@/shared/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { useDashboardCatalog } from "@/admin/hooks/use-dashboard-config";
import { getLiveSource } from "./live-sources";
import { TileBody } from "./dashboard-tiles";
import { LiveTable } from "./live-table";
import type {
  DataType,
  RenderType,
  TileDef,
  TileSpan,
} from "./dashboard-types";

// Which display types (render_type) a given data shape (data_type) supports. The
// chosen data's shape drives the display picker, so only sensible combinations
// are offered (a number can't be a map, a list can't be a pie, etc.).
const RENDER_BY_DATATYPE: Record<string, RenderType[]> = {
  snapshot_number: ["stat"],
  time_series: ["line_chart", "bar_chart", "table"],
  split_totals: ["bar_chart", "pie_chart", "leaderboard", "table"],
  worklist: ["table"],
  geo_points: ["map", "table"],
  action_list: ["alerts", "followups"],
  // Custom views draw only as their one renderer.
  notifications: ["alerts"],
  work_queue: ["work_queue"],
  activity: ["activity"],
  content: ["stat"],
};

const RENDER_LABEL: Record<RenderType, string> = {
  stat: "Number",
  table: "Table",
  leaderboard: "Leaderboard",
  bar_chart: "Bar chart",
  pie_chart: "Pie / donut",
  line_chart: "Line / trend",
  map: "Map",
  action_grid: "Action grid",
  followups: "List",
  alerts: "Alerts feed",
  work_queue: "Work queue",
  activity: "Activity feed",
};

const SPAN_OPTIONS: { value: TileSpan; label: string }[] = [
  { value: 4, label: "One third" },
  { value: 6, label: "Half" },
  { value: 8, label: "Two thirds" },
  { value: 12, label: "Full width" },
];

// Placeholder key a draft card carries during render; swapped for a real unique
// key when the card is saved.
const NEW_CARD_KEY = "u_new";

// A unique key for a user-created card. crypto.randomUUID is undefined in
// non-secure contexts / some test envs, so fall back. Called from the save
// handler (an event), never during render.
function newCardKey(): string {
  const raw =
    crypto?.randomUUID?.() ??
    `${Date.now().toString(36)}${Math.random().toString(36).slice(2)}`;
  return `u_${raw.slice(0, 8)}`;
}

// A card's title is inferred from its data (entity + measure), never typed. Most
// metrics read cleanly as a standalone phrase; the rest fall back to
// "<Entity> <measure>".
const TITLE_BY_MEASURE: Record<string, (entityLabel: string) => string> = {
  list: (e) => e,
  total: (e) => e,
  alerts: () => "Alerts",
  work_queue: () => "Work queue",
  activity: () => "Recent activity",
  active: (e) => `Active ${e.toLowerCase()}`,
  new_this_month: (e) => `${e} this month`,
  distinct_products: () => "Products covered",
  total_amount: () => "Total amount claimed",
  total_goodwill: () => "Total goodwill",
  average_amount: () => "Average payout",
};
// Measures whose catalogue label is already a complete phrase in the tenant's
// own partner vocabulary (resolved server-side, e.g. "Active Dealers" /
// "By Dealers"). Use the label verbatim so a custom card reads the same word as
// the seeded partner-network tiles instead of a hardcoded "contractor".
const MEASURES_USING_LABEL = new Set(["distinct_dealers", "by_dealer"]);
function inferTitle(
  entityLabel: string,
  measureKey: string,
  measureLabel: string,
): string {
  if (!entityLabel) return "Card";
  if (MEASURES_USING_LABEL.has(measureKey)) return measureLabel;
  const fn = TITLE_BY_MEASURE[measureKey];
  return fn ? fn(entityLabel) : `${entityLabel} ${measureLabel.toLowerCase()}`;
}

// A tile rendered exactly as it would appear on the dashboard, for the builder's
// live preview. Mirrors the TileCard body dispatch (live source -> LiveTable).
function TilePreview({
  tile,
  createdAfter,
}: {
  tile: TileDef;
  createdAfter?: string | null;
}) {
  const live = getLiveSource(tile.data_set?.source);
  return (
    <section className="cv-tile relative rounded-[15px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-card)] p-5">
      <div className="mb-3 text-[11.5px] font-medium uppercase tracking-[0.12em] text-[var(--ink-2)]">
        {tile.title || "Untitled"}
      </div>
      {live ? (
        <LiveTable
          key={`preview:${tile.key}:${tile.data_set?.source}`}
          source={tile.data_set!.source!}
          binding={tile.data_set!}
          tableId={`dashboard-preview-${tile.key}`}
        />
      ) : (
        <TileBody tile={tile} onDrill={() => {}} createdAfter={createdAfter} />
      )}
    </section>
  );
}

export interface CardEditorProps {
  open: boolean;
  onClose: () => void;
  /** Visible tile keys, in order. */
  order: string[];
  /** The merged tile lookup (config + user cards). */
  tiles: Record<string, TileDef>;
  createdAfter?: string | null;
  /** Add or update a card (keyed by tile.key). */
  onSave: (tile: TileDef) => void;
  /** Remove a card from the dashboard. */
  onDelete: (key: string) => void;
  /** Enter the drag-to-rearrange mode on the dashboard. */
  onRearrange: () => void;
}

export function CardEditor({
  open,
  onClose,
  order,
  tiles,
  createdAfter,
  onSave,
  onDelete,
  onRearrange,
}: CardEditorProps) {
  const { t } = useTranslation();
  const { data: catalog } = useDashboardCatalog();
  const entities = useMemo(() => catalog?.entities ?? [], [catalog]);
  const measures = useMemo(() => catalog?.measures ?? [], [catalog]);

  const [mode, setMode] = useState<"list" | "form">("list");
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [entity, setEntity] = useState("");
  const [measure, setMeasure] = useState("");
  const [renderType, setRenderType] = useState<RenderType | "">("");
  const [span, setSpan] = useState<TileSpan>(6);

  const entityDef = entities.find((e) => e.key === entity);
  const measureDef = measures.find((m) => m.key === measure);
  const dataType = measureDef?.data_type ?? "";
  const renderOptions: RenderType[] = dataType
    ? (RENDER_BY_DATATYPE[dataType] ?? [])
    : [];

  // A card is built from any entity x any measure the entity can fulfil. The
  // Measure list is scoped to the chosen entity's supported measures (from the
  // catalogue) so a combination never lands on an empty data path. Falls back to
  // every renderable measure until an entity is chosen.
  const availableMeasures = useMemo(() => {
    const renderable = measures.filter(
      (m) => (RENDER_BY_DATATYPE[m.data_type] ?? []).length > 0,
    );
    if (!entityDef) return renderable;
    const allow = new Set(entityDef.measures);
    return renderable.filter((m) => allow.has(m.key));
  }, [measures, entityDef]);

  // Switching entity: keep the measure if the new entity supports it, else clear
  // the measure + display so the user re-picks a valid one.
  const pickEntity = (e: string) => {
    setEntity(e);
    const allow = new Set(entities.find((x) => x.key === e)?.measures ?? []);
    if (measure && !allow.has(measure)) {
      setMeasure("");
      setRenderType("");
    }
  };

  const startAdd = () => {
    setEditingKey(null);
    setEntity("");
    setMeasure("");
    setRenderType("");
    setSpan(6);
    setMode("form");
  };
  const startEdit = (key: string) => {
    const t = tiles[key];
    if (!t) return;
    let ent = "";
    let meas = "";
    if (t.data_set?.source) {
      ent = t.data_set.source;
      meas = "list";
    } else if (t.data_set?.key && t.data_set.key.includes(":")) {
      const [e, m] = t.data_set.key.split(":");
      ent = e;
      meas = m;
    }
    setEditingKey(key);
    setEntity(ent);
    setMeasure(meas);
    setRenderType(t.render_type);
    setSpan(t.span);
    setMode("form");
  };
  // Changing entity keeps the chosen measure; changing measure defaults the
  // display to the first the new shape supports.
  const pickMeasure = (m: string) => {
    setMeasure(m);
    const dt = measures.find((x) => x.key === m)?.data_type ?? "";
    const opts = dt ? (RENDER_BY_DATATYPE[dt] ?? []) : [];
    setRenderType((prev) =>
      prev && opts.includes(prev) ? prev : (opts[0] ?? ""),
    );
  };

  const draft: TileDef | null =
    entityDef && measureDef && renderType
      ? {
          // A stable placeholder key during render; the real unique key is minted
          // in save() (an event, not render - so no impure call while rendering).
          key: editingKey ?? NEW_CARD_KEY,
          span,
          // Preview only - stripped in save(). A derived tile's title is
          // built server-side on every read from the tenant's vocabulary;
          // freezing it here is what left renamed tenants reading the
          // platform's word forever.
          title: inferTitle(entityDef.label, measureDef.key, measureDef.label),
          data_type: (measure === "list" ? "worklist" : dataType) as DataType,
          render_type: renderType,
          data_set:
            measure === "list"
              ? { source: entity, limit: 8 }
              : { key: `${entity}:${measure}` },
        }
      : null;

  const save = () => {
    if (!draft) return;
    // Persist WITHOUT the title: it is preview-only. The server builds a
    // derived tile's title on every read from the tenant's vocabulary, so a
    // stored copy would just be a snapshot that goes stale on a rename.
    const persisted: TileDef = { ...draft };
    delete persisted.title;
    onSave(
      persisted.key === NEW_CARD_KEY
        ? { ...persisted, key: newCardKey() }
        : persisted,
    );
    setMode("list");
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) {
          setMode("list");
          onClose();
        }
      }}
    >
      <DialogContent className="max-w-[860px] sm:max-w-[860px]">
        <DialogHeader>
          <DialogTitle>
            {mode === "list"
              ? t("dashboard.customise.title", "Customise dashboard")
              : editingKey
                ? t("dashboard.customise.editCard", "Edit card")
                : t("dashboard.customise.addCard", "Add a card")}
          </DialogTitle>
          <DialogDescription>
            {mode === "list"
              ? t(
                  "dashboard.customise.descriptionList",
                  "Add, edit, or remove the cards on your dashboard. Changes save to your profile.",
                )
              : t(
                  "dashboard.customise.descriptionForm",
                  "Pick the data and how to display it. The preview updates as you go.",
                )}
          </DialogDescription>
        </DialogHeader>

        {mode === "list" ? (
          <div className="flex flex-col gap-2">
            <div className="max-h-[52vh] overflow-y-auto pr-1">
              {order.length === 0 && (
                <p className="py-6 text-center text-[13px] text-[var(--ink-3)]">
                  No cards yet.
                </p>
              )}
              {order.map((key) => {
                const t = tiles[key];
                if (!t) return null;
                return (
                  <div
                    key={key}
                    className="flex items-center gap-3 border-b-[0.5px] border-[var(--rule)] py-2.5 last:border-b-0"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-[13.5px] font-medium text-[var(--ink)]">
                        {t.title}
                      </div>
                      <div className="text-[11.5px] text-[var(--ink-3)]">
                        {RENDER_LABEL[t.render_type] ?? t.render_type}
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      onClick={() => startEdit(key)}
                      aria-label={`Edit ${t.title}`}
                      title="Edit"
                      className="grid h-8 w-8 place-items-center rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-card)] p-0 text-[var(--ink-2)] hover:border-[var(--green-border)] hover:text-[var(--green)]"
                    >
                      <Pencil size={14} />
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={() => onDelete(key)}
                      aria-label={`Delete ${t.title}`}
                      title="Delete"
                      className="grid h-8 w-8 place-items-center rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-card)] p-0 text-[var(--ink-2)] hover:border-[var(--status-error)] hover:text-[var(--status-error)]"
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                );
              })}
            </div>
            <div className="flex items-center justify-between gap-2 pt-2">
              <Button
                variant="ghost"
                onClick={() => {
                  onClose();
                  onRearrange();
                }}
                className="flex items-center gap-2 text-[13px] font-normal text-[var(--ink-2)]"
              >
                <LayoutGrid size={15} /> Rearrange &amp; resize on the dashboard
              </Button>
              <Button
                onClick={startAdd}
                className="flex items-center gap-2 bg-[var(--green)] text-white hover:bg-[var(--green-hover)] hover:text-white"
              >
                <Plus size={15} /> Add a card
              </Button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-[300px_1fr]">
            {/* Builder: any entity x any measure - open combinations. Two tidy
                groups (what data / how to show it), full-width fields. */}
            <div className="flex flex-col gap-4">
              <div className="text-[10.5px] font-medium uppercase tracking-[0.14em] text-[var(--ink-3)]">
                Data
              </div>
              <div className="flex flex-col gap-1.5">
                <Label>Source</Label>
                <Select value={entity} onValueChange={pickEntity}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Choose data" />
                  </SelectTrigger>
                  <SelectContent>
                    {entities.map((e) => (
                      <SelectItem key={e.key} value={e.key}>
                        {e.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-col gap-1.5">
                <Label>Measure</Label>
                <Select
                  value={measure}
                  onValueChange={pickMeasure}
                  disabled={!entity}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Choose a measure" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableMeasures.map((m) => (
                      <SelectItem key={m.key} value={m.key}>
                        {m.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <Separator className="my-1" />

              <div className="text-[10.5px] font-medium uppercase tracking-[0.14em] text-[var(--ink-3)]">
                Presentation
              </div>
              <div className="flex flex-col gap-1.5">
                <Label>Display as</Label>
                <Select
                  value={renderType}
                  onValueChange={(v) => setRenderType(v as RenderType)}
                  disabled={!measure}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Choose a display" />
                  </SelectTrigger>
                  <SelectContent>
                    {renderOptions.map((rt) => (
                      <SelectItem key={rt} value={rt}>
                        {RENDER_LABEL[rt]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-col gap-1.5">
                <Label>Width</Label>
                <Select
                  value={String(span)}
                  onValueChange={(v) => setSpan(Number(v) as TileSpan)}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SPAN_OPTIONS.map((s) => (
                      <SelectItem key={s.value} value={String(s.value)}>
                        {s.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="mt-2 flex items-center justify-between gap-2">
                <Button
                  variant="ghost"
                  onClick={() => setMode("list")}
                  className="flex items-center gap-1.5 text-[13px] font-normal text-[var(--ink-2)]"
                >
                  <ArrowLeft size={15} /> Back
                </Button>
                <Button
                  onClick={save}
                  disabled={!draft}
                  className="bg-[var(--green)] text-white hover:bg-[var(--green-hover)] hover:text-white"
                >
                  {editingKey ? "Save changes" : "Add card"}
                </Button>
              </div>
            </div>

            {/* Live preview */}
            <div className="min-w-0">
              <div className="mb-2 text-[10.5px] font-medium uppercase tracking-[0.13em] text-[var(--ink-3)]">
                Preview
              </div>
              {draft ? (
                <TilePreview tile={draft} createdAfter={createdAfter} />
              ) : (
                <div className="grid h-[200px] place-items-center rounded-[15px] border-[0.5px] border-dashed border-[var(--rule)] text-[13px] text-[var(--ink-3)]">
                  {t(
                    "dashboard.customise.pickPreview",
                    "Pick data and a display to preview.",
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
